/**
 * flashbookClassReport.ts — a book's results for the person who set it.
 *
 * The learner's report (`#/lib/flashbookReport`) answers "how did I do, and what
 * should I look at again". This file answers the teacher's question instead: *what
 * do I teach tomorrow?* That is a different question with a different shape of
 * answer. A learner wants their own eight questions; a teacher wants the two the
 * class could not do and the names of the learners who missed them — because the
 * rest of the book went fine and re-teaching it wastes the lesson.
 *
 * Four decisions carry it.
 *
 *  - **One learner, one attempt.** A learner may have played the book five times.
 *    Counting all five would make a question look hard because one keen learner
 *    happened to get it wrong three times, and would let a single learner outvote
 *    their class. So each learner contributes *one* attempt — the most recent —
 *    and the plays that were passed over are still counted, as `attempts`, so a
 *    teacher can see who practised and who turned up once. The list is expected in
 *    time order (oldest first); the query does that ordering, so this file never
 *    has to parse a timestamp.
 *  - **A claim needs evidence.** Naming a question "hardest" off a single answer is
 *    a statement about one learner, not about the class, so a question has to have
 *    been answered by `MIN_ANSWERS` learners to be ranked at all. Everything else
 *    is still *shown* in the full question list, as data rather than a verdict.
 *  - **Skipped is not wrong.** The learner's report makes this point about one
 *    reader; at class scale it is the difference between "this defeated them" and
 *    "we never got there" — the first is a lesson plan, the second is a scheduling
 *    problem. So a question nobody answered is not ranked as hard; it lands in
 *    `unreached`, in its own list, with its own number.
 *  - **It carries facts, not sentences.** Every insight is a kind plus its
 *    numbers and its people; the wording lives in the screen behind Lingui. A model
 *    that emitted English prose could not be translated, and one that emitted a
 *    single class average could not tell a teacher *which* question to open with.
 *
 * Everything is derived from the book and the plays; nothing is stored. A book
 * edited after a play still reports against the page as it is today, because the
 * walk is over the *book* and the results are looked up in it — which also makes a
 * result whose block no longer exists quietly stale rather than an error.
 */
import {formatCorrectAnswer, formatUserAnswer} from '#/lib/answerFormat'
import {
  bookDirection,
  type FlashBookDirection,
  pageDirection,
  slideDirection,
} from '#/lib/flashbookDirection'
import {
  bandFor,
  blockPrompt,
  isScoredBlock,
  type ReportBand,
  slideTypeLabel,
} from '#/lib/flashbookReport'
import {getSlideMaxPoints} from '#/lib/scoring'
import {type Slide} from '#/types/capsule'
import {type FlashBookPage, type FlashBookPageResult} from '#/types/flashbook'

/**
 * The fewest learners that have to have answered a question before its result
 * means anything about the class.
 *
 * One wrong answer is a learner; two is the beginning of a pattern. Lowering this
 * to one would let the report call a question "hardest" on the strength of a
 * single person's typing, which is exactly the noise a class report exists to cut
 * through.
 */
export const MIN_ANSWERS = 2

/** A learner's play of the book, as the AppView lists it. */
export type ClassPlay = {
  learnerDid: string
  learnerHandle?: string
  /** What the learner is called, when the server knows. */
  learnerName?: string
  /**
   * The learner's own score, as the server stored it.
   *
   * Carried but never trusted for ranking: the report recomputes every number from
   * the book and the page results, so a play saved before a block was fixed cannot
   * disagree with the questions printed beside it.
   */
  score?: number
  timeMs?: number
  pageResults: FlashBookPageResult[]
}

/** Which learner, and what they are called in the report. */
export type ClassLearnerRef = {
  did: string
  /** The learner's name if the server has one, else their handle, else their DID. */
  name: string
  handle: string | null
}

/** A learner who got a question wrong, and what they answered. */
export type ClassMissRow = ClassLearnerRef & {
  /** Their answer as written. `'—'` when the app cannot render it. */
  answer: string
  /** Some of the question was right — a partial score, not a blank. */
  partial: boolean
}

/** One scored question of the book, as the whole class did on it. */
export type ClassQuestionRow = {
  pageIndex: number
  pageTitle: string
  pageDirection: FlashBookDirection
  direction: FlashBookDirection
  blockId: string
  type: string
  label: string
  prompt: string
  correctAnswer: string
  /** Learners who answered it — not the learners who reached the page. */
  answered: number
  correct: number
  wrong: number
  /**
   * Learners who never answered it — including those who never reached the page.
   *
   * Deliberately not folded into `wrong`: this number is about how far the class
   * got, and a teacher reading it as failure would be reading a timetable.
   */
  skipped: number
  /** 0–1: wrong over answered. 0 when nobody answered, because there is no rate. */
  wrongRate: number
  /** Everyone who has it outstanding, with what they wrote, in book order. */
  missedBy: ClassMissRow[]
  /** Everyone who got it, so a teacher can ask the class rather than tell it. */
  correctBy: ClassLearnerRef[]
  /** Everyone who never got to it. */
  skippedBy: ClassLearnerRef[]
  /**
   * What the ranker reads, so a teacher can see why a question is at the top.
   *
   * `false` for a question too few learners answered, and for one nobody answered:
   * both are shown in the list, and neither is called hard.
   */
  ranked: boolean
}

/** One block type, as the class managed it. */
export type ClassSkillRow = {
  type: string
  label: string
  exercises: number
  answered: number
  correct: number
  wrong: number
  /** Correct over answered, across the class. 0 when nothing was answered. */
  accuracy: number
  band: ReportBand
}

/** One learner, as they stand in the class. */
export type ClassLearnerRow = ClassLearnerRef & {
  /** Plays the server has from this learner. A learner may have read it twice. */
  attempts: number
  /** Of the book's exercises, the ones this learner answered. */
  answered: number
  correct: number
  wrong: number
  skipped: number
  points: number
  max: number
  /** 0–100 of the book's reachable points. */
  pct: number
  /** 0–100: the share of the book's pages this learner put work into. */
  completion: number
  /** Pages with work on them, 1-based count from the front of the book. */
  pagesReached: number
  timeMs: number
  band: ReportBand
  /** Questions this learner has outstanding, in book order. */
  missedBlockIds: string[]
}

/** A score band and how many learners landed in it. */
export type ClassBucket = {
  /** Inclusive lower bound, exclusive upper bound, in percent. 100 is closed. */
  from: number
  to: number
  learners: number
}

export type ClassInsight =
  /** The question the class did worst on: the one to open the next lesson with. */
  | {kind: 'hardest'; wrong: number; of: number; question: ClassQuestionRow}
  /** A question several learners answered and *nobody* got right. */
  | {kind: 'nobody'; question: ClassQuestionRow}
  /**
   * A question that split the class — roughly half right.
   *
   * Kept apart from `hardest` because it asks for a different lesson: not "go over
   * this again" but "argue about this", since half the room already has it.
   */
  | {kind: 'split'; question: ClassQuestionRow; wrongRate: number}
  /** The block type the class lost the most questions to. */
  | {kind: 'weakestType'; skill: ClassSkillRow}
  /** Questions not one learner answered — the class did not get that far. */
  | {kind: 'unreached'; count: number; of: number}
  /** The widest gap between any two learners, when it is worth acting on. */
  | {
      kind: 'spread'
      top: ClassLearnerRow
      bottom: ClassLearnerRow
      gap: number
    }
  /** The learner doing worst who did the work — not one who never turned up. */
  | {kind: 'needsHelp'; learner: ClassLearnerRow}

export type FlashBookClassReportModel = {
  /** Nobody has played the book, or the server has no plays to give. */
  empty: boolean
  overview: {
    /** Plays received, counting every learner's goes. */
    plays: number
    learners: number
    /** Mean of the learners' own percentages, not a pooled point count. */
    meanPct: number
    medianPct: number
    /** Learners who put work into every page of the book. */
    finished: number
    /** Mean time per learner, over the learners who have one. */
    meanTimeMs: number
    /** Questions in the book that at least one learner answered. */
    questionsTouched: number
    questions: number
  }
  distribution: ClassBucket[]
  learners: ClassLearnerRow[]
  /** Every scored question in the book, in book order. */
  questions: ClassQuestionRow[]
  /**
   * The questions worth teaching, worst first — the whole point of the report.
   *
   * Only questions with evidence are in here (see `MIN_ANSWERS`), ordered by how
   * many learners it cost rather than by rate, because a question twelve learners
   * missed is worth a lesson and one two learners missed is worth a minute.
   */
  hardest: ClassQuestionRow[]
  /** Questions not one learner answered, in book order. */
  unreached: ClassQuestionRow[]
  skills: ClassSkillRow[]
  insights: ClassInsight[]
}

/** The share of a group that got something right, as a `ReportBand`. */
function accuracyBand(answered: number, correct: number, exercises: number) {
  return bandFor({answered, correct, exercises})
}

/** What to call a learner when the server has told us nothing. */
function learnerLabel(play: ClassPlay): string {
  const name = play.learnerName?.trim()
  if (name) return name
  const handle = play.learnerHandle?.trim()
  if (handle) return handle
  // A DID's tail is not a name, but it is what the server knows and it is stable
  // enough to say "this one" with (`did:plc:abcdef…` → `plc:abcdef`).
  return play.learnerDid.replace(/^did:/, '')
}

/** A learner, referenced by value rather than by position. */
function refOf(row: ClassLearnerRow): ClassLearnerRef {
  return {did: row.did, name: row.name, handle: row.handle}
}

/**
 * The score bands the class is spread over.
 *
 * Five buckets, fixed and inclusive of the bottom, because a histogram whose edges
 * move with the data cannot be compared between two books — and comparing this
 * week's book against last week's is most of what a teacher does with a report.
 */
const BUCKETS: {from: number; to: number}[] = [
  {from: 0, to: 25},
  {from: 25, to: 50},
  {from: 50, to: 75},
  {from: 75, to: 100},
]

/** How wide a gap between two learners is worth naming. */
const SPREAD_MIN = 25

/** How many insights a teacher gets before it becomes a wall of text. */
const INSIGHT_LIMIT = 5

/**
 * Builds the class's report from the book and the plays the server returned.
 *
 * `plays` is expected oldest first; a learner's last entry in the list is the
 * attempt the report is about. Empty input is not an error — it is a book nobody
 * has played yet, which the report says in its own words rather than showing a wall
 * of zeroes.
 */
export function buildFlashBookClassReport({
  pages,
  plays,
}: {
  pages: readonly FlashBookPage[]
  plays: readonly ClassPlay[]
}): FlashBookClassReportModel {
  const bookDir = bookDirection(pages)

  /**
   * Every learner's latest attempt, which is the one the report is about.
   *
   * A `Map` keeps insertion order, so the learners come out in the order the server
   * listed them on their *first* play rather than their last, which is stable
   * across refetches and does not reshuffle the roster when one learner practises.
   */
  const latest = new Map<string, {play: ClassPlay; attempts: number}>()
  for (const play of plays) {
    const previous = latest.get(play.learnerDid)
    latest.set(play.learnerDid, {
      play,
      attempts: (previous?.attempts ?? 0) + 1,
    })
  }

  /** Each learner's results, keyed for the question walk below. */
  const resultsByLearner = new Map<string, Map<string, FlashBookPageResult>>()
  for (const [did, {play}] of latest) {
    const byPage = new Map<string, FlashBookPageResult>()
    for (const page of play.pageResults) byPage.set(page.pageId, page)
    resultsByLearner.set(did, byPage)
  }

  const rows: ClassLearnerRow[] = []
  for (const [did, {play, attempts}] of latest) {
    rows.push({
      did,
      name: learnerLabel(play),
      handle: play.learnerHandle?.trim() || null,
      attempts,
      answered: 0,
      correct: 0,
      wrong: 0,
      skipped: 0,
      points: 0,
      max: 0,
      pct: 0,
      completion: 0,
      pagesReached: 0,
      timeMs: play.timeMs ?? 0,
      band: 'unscored',
      missedBlockIds: [],
    })
  }
  const questions: ClassQuestionRow[] = []
  const skills = new Map<string, ClassSkillRow>()
  let questionsTouched = 0

  pages.forEach((page, pageIndex) => {
    const title = page.title?.trim() || ''
    const pageDir = pageDirection(page.blocks) ?? bookDir

    for (const block of page.blocks) {
      if (!isScoredBlock(block)) continue

      const blockMax = getSlideMaxPoints(block)
      const question: ClassQuestionRow = {
        pageIndex,
        pageTitle: title,
        pageDirection: pageDir,
        direction: slideDirection(block) ?? pageDir,
        blockId: block.id,
        type: block.type,
        label: slideTypeLabel(block.type),
        prompt: blockPrompt(block),
        correctAnswer: formatCorrectAnswer(block),
        answered: 0,
        correct: 0,
        wrong: 0,
        skipped: 0,
        wrongRate: 0,
        missedBy: [],
        correctBy: [],
        skippedBy: [],
        ranked: false,
      }

      const skill = skills.get(block.type) ?? {
        type: block.type,
        label: slideTypeLabel(block.type),
        exercises: 0,
        answered: 0,
        correct: 0,
        wrong: 0,
        accuracy: 0,
        band: 'unscored' as ReportBand,
      }
      skill.exercises += 1

      /*
       * Walk the learners rather than the results, once per question.
       *
       * The other way round — iterating each learner's results — would let a block
       * that was deleted from the book still be counted, and would make "nobody
       * answered this" indistinguishable from "this learner has no result for it".
       * Asking each learner about *this* question is what makes those two facts
       * arrive separately.
       */
      for (const row of rows) {
        const pageResult = resultsByLearner.get(row.did)?.get(page.id)
        const result = pageResult?.blocks.find(b => b.blockId === block.id)
        const wasAnswered = !!result && result.isCorrect !== undefined

        row.max += blockMax
        if (!wasAnswered) {
          row.skipped += 1
          question.skipped += 1
          question.skippedBy.push(refOf(row))
          continue
        }

        row.answered += 1
        question.answered += 1
        skill.answered += 1
        row.points += result.points ?? 0
        if (result.isCorrect) {
          row.correct += 1
          question.correct += 1
          skill.correct += 1
          question.correctBy.push(refOf(row))
        } else {
          row.wrong += 1
          question.wrong += 1
          skill.wrong += 1
          row.missedBlockIds.push(block.id)
          question.missedBy.push({
            ...refOf(row),
            answer: classUserAnswer(block, result.userAnswer),
            partial: (result.points ?? 0) > 0,
          })
        }
      }

      if (question.answered > 0) questionsTouched += 1
      question.wrongRate =
        question.answered > 0 ? question.wrong / question.answered : 0
      question.ranked = question.answered >= MIN_ANSWERS && question.wrong > 0
      questions.push(question)
      skills.set(block.type, skill)
    }

    /*
     * How far each learner got, tracked over *pages* rather than questions.
     *
     * A learner who answered one question on page 9 and nothing on pages 5–8 has
     * reached page 9, and a completion figure that counted a contiguous run would
     * call them a page-9 reader who skipped the middle — which is not how reading a
     * book works, and not what a teacher needs to know.
     */
    for (const row of rows) {
      const pageResult = resultsByLearner.get(row.did)?.get(page.id)
      const worked = !!pageResult?.blocks.some(b => b.isCorrect !== undefined)
      if (worked && row.pagesReached < pageIndex + 1) {
        row.pagesReached = pageIndex + 1
      }
    }
  })

  for (const row of rows) {
    row.pct =
      row.max > 0 ? Math.min(100, Math.round((row.points / row.max) * 100)) : 0
    row.completion =
      pages.length > 0 ? Math.round((row.pagesReached / pages.length) * 100) : 0
    row.band = accuracyBand(
      row.answered,
      row.correct,
      row.answered + row.skipped,
    )
  }

  for (const skill of skills.values()) {
    skill.accuracy = skill.answered > 0 ? skill.correct / skill.answered : 0
    skill.band = accuracyBand(skill.answered, skill.correct, skill.exercises)
  }

  const hardest = questions
    .filter(question => question.ranked)
    .sort(
      (a, b) =>
        b.wrong - a.wrong ||
        b.wrongRate - a.wrongRate ||
        a.pageIndex - b.pageIndex,
    )

  const unreached = questions.filter(question => question.answered === 0)

  const skillList = [...skills.values()].sort(
    (a, b) =>
      b.wrong - a.wrong ||
      a.accuracy - b.accuracy ||
      b.exercises - a.exercises ||
      a.label.localeCompare(b.label),
  )

  const scored = rows.filter(row => row.answered > 0)
  const pcts = scored.map(row => row.pct).sort((a, b) => a - b)
  const meanPct =
    pcts.length > 0
      ? Math.round(pcts.reduce((a, p) => a + p, 0) / pcts.length)
      : 0
  const medianPct =
    pcts.length > 0
      ? pcts.length % 2 === 1
        ? pcts[(pcts.length - 1) / 2]
        : Math.round((pcts[pcts.length / 2 - 1] + pcts[pcts.length / 2]) / 2)
      : 0
  const timed = rows.filter(row => row.timeMs > 0)
  const finished = rows.filter(
    row => pages.length > 0 && row.pagesReached >= pages.length,
  ).length

  const model: FlashBookClassReportModel = {
    empty: plays.length === 0,
    overview: {
      plays: plays.length,
      learners: rows.length,
      meanPct,
      medianPct,
      finished,
      meanTimeMs:
        timed.length > 0
          ? Math.round(timed.reduce((a, r) => a + r.timeMs, 0) / timed.length)
          : 0,
      questionsTouched,
      questions: questions.length,
    },
    distribution: BUCKETS.map(bucket => ({
      ...bucket,
      // The top bucket is closed, because 100% has nowhere above it and a learner
      // who scored it would otherwise be in no bucket at all.
      learners: scored.filter(row =>
        bucket.to === 100
          ? row.pct >= bucket.from && row.pct <= bucket.to
          : row.pct >= bucket.from && row.pct < bucket.to,
      ).length,
    })),
    learners: rows,
    questions,
    hardest,
    unreached,
    skills: skillList,
    insights: [],
  }

  model.insights = buildClassInsights(model).slice(0, INSIGHT_LIMIT)
  return model
}

/**
 * The learner's answer, as the teacher should see it.
 *
 * `formatUserAnswer` is the app's own renderer — the same one the learner's report
 * prints their answers with, so a teacher and their learner are looking at the same
 * words — and this only names a blank what it is: a wrong row showing an empty
 * string reads as a rendering bug rather than as an answer with nothing in it.
 */
function classUserAnswer(block: Slide, answer: unknown): string {
  const text = formatUserAnswer(block, answer)
  return text.trim() || '—'
}

/**
 * What a teacher should take away, most useful first.
 *
 * Ordered the way a lesson gets planned: the question that cost the most, then a
 * question nobody could do, then the one that split the room, then the block type
 * behind the losses, then who needs catching up. The cap is applied by the caller,
 * so a class of sixteen on a five-page book gets three lines instead of a report
 * padded out to a fixed length.
 */
function buildClassInsights(model: FlashBookClassReportModel): ClassInsight[] {
  const out: ClassInsight[] = []

  /*
   * A book nobody has played has nothing to say.
   *
   * Without this, an unplayed book would report "0 of 30 questions were answered"
   * — which is true and useless, and reads as a finding about the class rather
   * than as the fact that the work has not been set yet. The `empty` flag carries
   * that fact and the screen says it in one sentence.
   */
  if (model.empty) return out

  const worst = model.hardest[0]
  if (worst) {
    out.push({
      kind: 'hardest',
      wrong: worst.wrong,
      of: worst.answered,
      question: worst,
    })
  }

  /*
   * A question nobody got right, *with* the evidence bar: on a book one learner
   * played, "nobody got this" is a sentence about that learner, and the class
   * report exists precisely to stop saying those.
   */
  const nobody = model.hardest.find(q => q.correct === 0)
  if (nobody) out.push({kind: 'nobody', question: nobody})

  /*
   * The question that split the class: the closest to half, with evidence.
   *
   * Not simply "the second-hardest", because its lesson is the opposite one — half
   * the room already understands it, so the way in is to have them explain it.
   */
  const split = model.hardest
    .filter(q => q.wrongRate > 0.35 && q.wrongRate < 0.65)
    .sort(
      (a, b) =>
        Math.abs(a.wrongRate - 0.5) - Math.abs(b.wrongRate - 0.5) ||
        a.pageIndex - b.pageIndex,
    )[0]
  if (split)
    out.push({kind: 'split', question: split, wrongRate: split.wrongRate})

  const weakType = model.skills.find(
    skill => skill.wrong > 0 && skill.answered >= MIN_ANSWERS,
  )
  if (weakType) out.push({kind: 'weakestType', skill: weakType})

  if (model.unreached.length > 0) {
    out.push({
      kind: 'unreached',
      count: model.unreached.length,
      of: model.questions.length,
    })
  }

  /*
   * The two ends of the class, named only when the gap is wide enough to act on.
   *
   * A few points between learners is noise; twenty-five is two different lessons
   * happening in one room. Both ends have to have done the work, so a learner who
   * opened page 1 and stopped is not printed as "bottom of the class".
   */
  const scored = model.learners.filter(row => row.answered > 0)
  if (scored.length >= 2) {
    const sorted = [...scored].sort((a, b) => b.pct - a.pct)
    const top = sorted[0]
    const bottom = sorted[sorted.length - 1]
    const gap = top.pct - bottom.pct
    if (gap >= SPREAD_MIN) {
      out.push({kind: 'spread', top, bottom, gap})
    }
    if (bottom.pct < 50) {
      out.push({kind: 'needsHelp', learner: bottom})
    }
  }

  return out
}

/** The learners who missed a question, for a caller that wants only the names. */
export function missedNames(question: ClassQuestionRow): string[] {
  return question.missedBy.map(miss => miss.name)
}
